// tslint:disable: no-floating-promises
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

import { IErrors } from "pf_styleguide";

import { ConfigureService } from "../../../../../configure/configure.service";
import { Slot } from "../../../../../shared/model/slot";
import { AuthService } from "../../../../../shared/module/auth/auth.service";
import { IPayGuest } from "../../../../../shared/module/auth/IPayGuest";
import { PaymentFormService } from "../payment-form/payment-form.service";

import { DiscountData } from "../../../../../shared/model/discountData";

@Component({
    selector: "pf-discount",
    templateUrl: "discount.component.html",
    styleUrls: ["./discount.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscountComponent implements OnInit {
    @Input() slot: Slot;
    @Input() guest: IPayGuest;

    discountData: DiscountData;
    discountCodeError = false;
    discountCodeNotFound = false;

    discountCodeErrorMessage: string;
    freeSlotsDiscountCode: string;

    formGroup: FormGroup;

    constructor(
        public authService: AuthService,
        public cd: ChangeDetectorRef,
        public config: ConfigureService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly fb: FormBuilder,
        private readonly paymentFormService: PaymentFormService,
        private readonly router: Router
    ) {
        this.formGroup = this.fb.group({
            promoCode: [""],
        });

        this.freeSlotsDiscountCode = this.config.params.freeSlotsDiscountCode;
    }

    ngOnInit(): void {
        const promoCode = this.activatedRoute.snapshot.params.promoCode;

        if (promoCode && this.config.params.promoCodesEnabled) {
            this.formGroup.controls.promoCode.setValue(promoCode);
            this.applyPromoCode(promoCode, false);
        }

        if (
            this.slot &&
            this.slot.attributes.price === 0 &&
            this.activatedRoute.snapshot.params.slotId
        ) {
            this.processFreePromoCode(this.freeSlotsDiscountCode);
        }
    }

    isFree(): boolean {
        return this.slot && this.slot.attributes.price === 0;
    }

    processFreePromoCode(promoCode: string): void {
        const slotId = this.activatedRoute.snapshot.params.slotId;

        this.paymentFormService
            .getPromoCodeForSlot(
                promoCode,
                slotId,
                this.authService.user,
                this.guest ? this.guest.email : undefined
            )
            .subscribe(
                (data) => {
                    this.discountData = data;
                    this.cd.markForCheck();
                    this.formGroup.controls.promoCode.setValue(promoCode);
                    this.promoCodeChangeUrl(promoCode);
                },
                (_) => {
                    this.discountCodeErrorMessage = undefined;
                    this.discountData = undefined;
                }
            );
    }

    applyPromoCode(promoCode: string, button = false): void {
        const slotId = this.activatedRoute.snapshot.params.slotId;
        const triggerErrorInfo = this.canITriggerInfoError(button);

        this.discountCodeError = false;

        if (promoCode) {
            this.paymentFormService
                .getPromoCodeForSlot(
                    promoCode,
                    slotId,
                    this.authService.user,
                    this.guest ? this.guest.email : undefined
                )
                .subscribe(
                    (data) => {
                        if (!data.redeemable) {
                            this.discountCodeErrorMessage = data.error;
                            this.discountData = undefined;

                            if (triggerErrorInfo) {
                                this.discountCodeError = true;
                                this.cd.markForCheck();
                            }
                        } else {
                            this.discountData = data;
                            this.cd.markForCheck();
                        }
                    },
                    (_) => {
                        this.discountCodeErrorMessage = undefined;
                        this.discountData = undefined;

                        if (triggerErrorInfo) {
                            this.discountCodeNotFound = true;
                            this.cd.markForCheck();
                        }
                    }
                );
        } else {
            this.discountData = undefined;
        }

        if (button) this.promoCodeChangeUrl(promoCode);
    }

    removeDiscount(): void {
        this.discountData = undefined;
        this.router.navigate(["../../"], {
            relativeTo: this.activatedRoute,
            replaceUrl: true,
            queryParamsHandling: "preserve",
            fragment: "",
        });
    }

    getTotal(): number {
        return this.discountData
            ? this.discountData.discountPrice
            : +this.slot.attributes.price + +this.slot.attributes.admin_fee;
    }

    errors(): Array<IErrors> {
        let message: string;

        if (this.discountCodeNotFound) {
            message = "Unfortunately this discount code is invalid";
        }

        if (this.discountCodeError) {
            message = this.discountCodeErrorMessage;
        }

        return message ? [{ title: message }] : [];
    }

    private canITriggerInfoError(button: boolean | undefined): boolean {
        let triggerErrorInfo = true;

        if (button && !this.activatedRoute.snapshot.params.promoCode) {
            triggerErrorInfo = false;
        }

        return triggerErrorInfo;
    }

    private promoCodeChangeUrl(promoCode: string): void {
        if (promoCode) {
            if (this.activatedRoute.snapshot.params.promoCode) {
                this.router.navigate(
                    [`${this.slot.id}/discount/${promoCode}`],
                    {
                        relativeTo: this.activatedRoute.parent,
                        replaceUrl: true,
                        queryParamsHandling: "preserve",
                    }
                );
            } else {
                this.router.navigate([`discount/${promoCode}`], {
                    relativeTo: this.activatedRoute,
                    replaceUrl: true,
                    queryParamsHandling: "preserve",
                });
            }
        } else {
            if (this.activatedRoute.snapshot.params.promoCode) {
                this.router.navigate(["../../"], {
                    relativeTo: this.activatedRoute,
                    replaceUrl: true,
                    queryParamsHandling: "preserve",
                });
            }
        }
    }
}
